/**
 * ScoreColorBadge Component
 *
 * Displays a score with color-coded background based on the score value
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { getScoreColor, getScoreLabel } from '../utils/scoreUtils';

export interface ScoreColorBadgeProps {
    score: number;
    size?: 'small' | 'medium' | 'large';
    showLabel?: boolean;
    showPercentage?: boolean;
    variant?: 'filled' | 'outlined' | 'gradient';
}

const SIZE_STYLES = {
    small: {
        minWidth: 40,
        height: 24,
        fontSize: '0.75rem',
        fontWeight: 600,
        borderRadius: 6,
        px: 1,
    },
    medium: {
        minWidth: 56,
        height: 32,
        fontSize: '0.875rem',
        fontWeight: 700,
        borderRadius: 8,
        px: 1.5,
    },
    large: {
        minWidth: 72,
        height: 48,
        fontSize: '1.25rem',
        fontWeight: 800,
        borderRadius: 12,
        px: 2,
    },
};

export const ScoreColorBadge: React.FC<ScoreColorBadgeProps> = ({
    score,
    size = 'medium',
    showLabel = false,
    showPercentage = true,
    variant = 'filled',
}) => {
    const color = getScoreColor(score);
    const label = getScoreLabel(score);
    const sizeStyle = SIZE_STYLES[size];

    const getVariantStyles = () => {
        switch (variant) {
            case 'outlined':
                return {
                    bgcolor: `${color}15`,
                    border: `2px solid ${color}50`,
                    color: color,
                };
            case 'gradient':
                return {
                    background: `linear-gradient(135deg, ${color} 0%, ${color}bb 100%)`,
                    color: '#ffffff',
                    boxShadow: `0 4px 12px ${color}40`,
                };
            case 'filled':
            default:
                return {
                    bgcolor: color,
                    color: '#ffffff',
                    boxShadow: `0 2px 8px ${color}50`,
                };
        }
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
                sx={{
                    ...sizeStyle,
                    ...getVariantStyles(),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Typography
                    sx={{
                        fontSize: sizeStyle.fontSize,
                        fontWeight: sizeStyle.fontWeight,
                        lineHeight: 1,
                    }}
                >
                    {showPercentage ? `${Math.round(score)}%` : Math.round(score)}
                </Typography>
            </Box>
            {showLabel && (
                <Typography
                    variant="caption"
                    sx={{
                        color: color,
                        fontWeight: 600,
                        fontSize: size === 'small' ? '0.65rem' : '0.75rem',
                    }}
                >
                    {label}
                </Typography>
            )}
        </Box>
    );
};

export default ScoreColorBadge;
